import { CaretRight, CheckCircle, Drop, Fire } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import type { RecentAlarm } from "@/api/districts/types";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardAction,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { isValidLang, type Lang, ROUTE_SLUGS } from "@/config/routes";
import { formatDateTime } from "@/utils/format-datetime";

type Props = {
	alarms: RecentAlarm[] | undefined;
	isLoading: boolean;
};

function MeterTypeIcon({ meterType }: { meterType: string }) {
	if (meterType === "water")
		return (
			<Drop
				className="size-4 text-blue-600 dark:text-blue-400"
				aria-hidden="true"
			/>
		);
	if (meterType === "heat")
		return (
			<Fire
				className="size-4 text-orange-600 dark:text-orange-400"
				aria-hidden="true"
			/>
		);
	return null;
}

export function RecentAlertsSection({ alarms, isLoading }: Props) {
	const { t } = useTranslation();
	const { lang } = useParams<{ lang: string }>();
	const navigate = useNavigate();
	const currentLang: Lang = lang && isValidLang(lang) ? lang : "pl";
	const alertsPath = `/${currentLang}/${ROUTE_SLUGS.alerts[currentLang]}`;

	const isEmpty = !isLoading && (!alarms || alarms.length === 0);

	return (
		<Card className="bg-muted/30 flex h-full flex-col">
			<CardHeader>
				<CardTitle className="text-base">
					{t("dashboard.alerts.title")}
				</CardTitle>
				<CardAction>
					<Button variant="ghost" size="sm" asChild>
						<Link to={alertsPath}>{t("dashboard.alerts.viewAll")}</Link>
					</Button>
				</CardAction>
			</CardHeader>
			<CardContent className="flex min-h-0 flex-1 flex-col">
				{isLoading ? (
					<div
						role="status"
						aria-label={t("common.loading")}
						className="space-y-2"
					>
						{Array.from({ length: 3 }).map((_, i) => (
							<Skeleton key={i} className="h-14 w-full" />
						))}
					</div>
				) : isEmpty ? (
					<div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 py-8 text-center text-sm">
						<CheckCircle
							className="size-8 text-emerald-500"
							aria-hidden="true"
						/>
						<span>{t("dashboard.alerts.noAlerts")}</span>
					</div>
				) : (
					<div className="bg-background min-h-0 flex-1 overflow-y-auto rounded-md border">
						<ul className="divide-y">
							{alarms?.map((alarm) => (
								<li key={`${alarm.serial_number}-${alarm.cap_dt}`}>
									<button
										type="button"
										onClick={() =>
											navigate(
												`${alertsPath}?type=${alarm.meter_type}&search=${encodeURIComponent(
													alarm.serial_number,
												)}`,
											)
										}
										className="hover:bg-muted/50 group flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors sm:gap-3"
									>
										<span className="mt-0.5 shrink-0">
											<MeterTypeIcon meterType={alarm.meter_type} />
										</span>
										<div className="min-w-0 flex-1 space-y-0.5">
											<div className="flex items-baseline justify-between gap-2">
												<p className="min-w-0 truncate font-mono text-xs">
													{alarm.serial_number}
												</p>
												<span className="text-muted-foreground shrink-0 text-xs whitespace-nowrap tabular-nums">
													{formatDateTime(alarm.cap_dt, currentLang)}
												</span>
											</div>
											<p className="text-foreground text-sm break-words">
												{t(`dashboard.alertsBanner.alarmTypes.${alarm.alarm}`, {
													defaultValue: alarm.alarm,
												})}
											</p>
										</div>
										<CaretRight
											className="text-muted-foreground group-hover:text-foreground mt-0.5 size-4 shrink-0"
											aria-hidden="true"
										/>
									</button>
								</li>
							))}
						</ul>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
